<template>
    <div class="service-order-management">
        <div class="header">
            <h2>服务订单管理</h2>
        </div>

        <!-- 搜索筛选 -->
        <el-card class="search-card">
            <el-form :model="searchForm" inline>
                <el-form-item label="企业" v-if="isAdmin">
                    <el-select v-model="searchForm.enterpriseId" placeholder="请选择企业" clearable filterable
                        @change="handleSearch">
                        <el-option v-for="enterprise in enterprises" :key="enterprise.id" :label="enterprise.name"
                            :value="enterprise.id"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="状态">
                    <el-select v-model="searchForm.status" placeholder="订单状态" clearable @change="handleSearch">
                        <el-option label="待确认" value="PENDING"></el-option>
                        <el-option label="已支付" value="PAID"></el-option>
                        <el-option label="已驳回" value="REJECTED"></el-option>
                        <el-option label="已取消" value="CANCELLED"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="关键字">
                    <el-input v-model="searchForm.keyword" placeholder="搜索订单号/企业/服务" clearable
                        @keyup.enter.native="handleSearch"></el-input>
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" @click="handleSearch">搜索</el-button>
                    <el-button @click="handleReset">重置</el-button>
                </el-form-item>
            </el-form>
        </el-card>

        <!-- 订单列表 -->
        <el-table :data="orders" v-loading="loading" border style="width: 100%">
            <el-table-column prop="id" label="订单号" width="80"></el-table-column>
            <el-table-column prop="enterpriseName" label="关联企业" min-width="150" v-if="isAdmin"></el-table-column>
            <el-table-column prop="serviceName" label="购买服务" min-width="150"></el-table-column>
            <el-table-column prop="customerName" label="客户姓名" width="120"></el-table-column>
            <el-table-column prop="customerPhone" label="联系电话" width="120"></el-table-column>
            <el-table-column prop="amount" label="金额" width="100" align="right">
                <template slot-scope="scope">
                    <span style="color: #67C23A; font-weight: bold;">¥{{ formatAmount(scope.row.amount) }}</span>
                </template>
            </el-table-column>
            <el-table-column label="微信交易号" width="180" align="center">
                <template slot-scope="scope">
                    <span v-if="scope.row.transactionId" style="font-size: 12px;">{{ scope.row.transactionId }}</span>
                    <span v-else style="color: #909399; font-size: 12px;">待支付</span>
                </template>
            </el-table-column>
            <el-table-column prop="status" label="状态" width="100" align="center">
                <template slot-scope="scope">
                    <el-tag :type="getStatusType(scope.row.status)">
                        {{ getStatusLabel(scope.row.status) }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column prop="createdAt" label="下单时间" width="160">
                <template slot-scope="scope">
                    {{ formatDateTime(scope.row.createdAt) }}
                </template>
            </el-table-column>
            <el-table-column label="操作" width="160" fixed="right">
                <template slot-scope="scope">
                    <el-button size="mini" @click="handleView(scope.row)">详情</el-button>
                    <template v-if="scope.row.status === 'PENDING'">
                        <el-button size="mini" type="success" @click="handleVerify(scope.row, true)">通过</el-button>
                    </template>
                </template>
            </el-table-column>
        </el-table>

        <!-- 详情对话框 -->
        <el-dialog title="订单详情" :visible.sync="detailVisible" width="600px">
            <div v-if="currentOrder">
                <el-descriptions :column="1" border>
                    <el-descriptions-item label="订单编号">{{ currentOrder.id }}</el-descriptions-item>
                    <el-descriptions-item label="企业名称">{{ currentOrder.enterpriseName }}</el-descriptions-item>
                    <el-descriptions-item label="购买服务">{{ currentOrder.serviceName }}</el-descriptions-item>
                    <el-descriptions-item label="下单客户">{{ currentOrder.customerName }} ({{ currentOrder.customerPhone
                    }})</el-descriptions-item>
                    <el-descriptions-item label="支付金额">¥{{ formatAmount(currentOrder.amount) }}</el-descriptions-item>
                    <el-descriptions-item label="订单状态">
                        <el-tag :type="getStatusType(currentOrder.status)">{{ getStatusLabel(currentOrder.status)
                        }}</el-tag>
                    </el-descriptions-item>
                    <el-descriptions-item label="下单时间">{{ formatDateTime(currentOrder.createdAt)
                    }}</el-descriptions-item>
                    <el-descriptions-item label="备注">{{ currentOrder.remark || '无' }}</el-descriptions-item>
                </el-descriptions>

                <el-descriptions-item label="微信交易号">{{ currentOrder.transactionId || '待支付' }}</el-descriptions-item>
                <el-descriptions-item label="支付时间">{{ currentOrder.payTime ? formatDateTime(currentOrder.payTime) : '-' }}</el-descriptions-item>

                <div style="margin-top: 20px; text-align: right;" v-if="currentOrder.status === 'PENDING'">
                    <el-button type="danger" @click="handleVerify(currentOrder, false)">驳回订单</el-button>
                    <el-button type="success" @click="handleVerify(currentOrder, true)">确认收款</el-button>
                </div>
            </div>
        </el-dialog>


    </div>
</template>

<script>
export default {
    name: 'ServiceOrderManagement',
    data() {
        return {
            orders: [],
            enterprises: [],
            loading: false,
            detailVisible: false,
            currentOrder: null,
            searchForm: {
                enterpriseId: null,
                status: '',
                customerName: '',
                keyword: ''
            }
        }
    },
    computed: {
        isAdmin() {
            const role = localStorage.getItem('userRole')
            return role === 'ADMIN' || role === 'SUPER_ADMIN'
        }
    },
    mounted() {
        this.loadOrders()
        if (this.isAdmin) {
            this.loadEnterprises()
        }
    },
    methods: {
        async loadOrders() {
            try {
                this.loading = true
                const response = await this.$http.get('/service-orders', { params: this.searchForm })
                if (response.success) {
                    this.orders = response.data || []
                }
            } catch (error) {
                console.error('加载订单失败', error)
                this.$message.error('加载订单列表失败')
            } finally {
                this.loading = false
            }
        },
        async loadEnterprises() {
            try {
                const response = await this.$http.get('/enterprises')
                if (response.success) {
                    this.enterprises = response.data || []
                }
            } catch (error) {
                console.error('加载企业失败', error)
            }
        },
        handleSearch() {
            this.loadOrders()
        },
        handleReset() {
            this.searchForm = {
                enterpriseId: null,
                status: '',
                customerName: '',
                keyword: ''
            }
            this.loadOrders()
        },
        handleView(row) {
            this.currentOrder = row
            this.detailVisible = true
        },
        handleVerify(order, approved) {
            const action = approved ? '确认已收款' : '驳回订单';
            this.$confirm(`确定要执行 ${action} 吗？`, '审核提示', {
                type: approved ? 'success' : 'warning'
            }).then(async () => {
                try {
                    let rejectReason = '';
                    if (!approved) {
                        const { value } = await this.$prompt('请输入驳回原因', '提示', {
                            confirmButtonText: '确定',
                            cancelButtonText: '取消'
                        });
                        rejectReason = value;
                    }

                    const response = await this.$http.post(`/enterprise/service-orders/${order.id}/verify`, {
                        approved,
                        rejectReason
                    });

                    if (response.success) {
                        this.$message.success('审核成功');
                        this.detailVisible = false;
                        this.loadOrders();
                    } else {
                        this.$message.error(response.message || '操作失败');
                    }
                } catch (error) {
                    if (error !== 'cancel') {
                        this.$message.error('操作异常');
                    }
                }
            });
        },

        getStatusLabel(status) {
            const map = {
                'PENDING': '待确认',
                'PAID': '已支付',
                'REJECTED': '已驳回',
                'CANCELLED': '已取消'
            };
            return map[status] || status;
        },
        getStatusType(status) {
            const map = {
                'PENDING': 'warning',
                'PAID': 'success',
                'REJECTED': 'danger',
                'CANCELLED': 'info'
            };
            return map[status] || 'info';
        },
        formatAmount(amount) {
            return Number(amount || 0).toFixed(2);
        },
        formatDateTime(dateTime) {
            if (!dateTime) return '-';
            return new Date(dateTime).toLocaleString('zh-CN');
        }
    }
}
</script>

<style scoped lang="scss">
.service-order-management {
    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;

        h2 {
            margin: 0;
            font-size: 24px;
            color: #333;
        }
    }

    .search-card {
        margin-bottom: 20px;
    }

    .proof-section {
        margin-top: 20px;
        padding-top: 10px;
        border-top: 1px dashed #DDD;

        h4 {
            margin: 0 0 10px;
            color: #606266;
        }
    }
}
</style>
